import React, { useState, useEffect } from 'react';
import QrScanner from 'react-qr-scanner';
import { X, QrCode, Keyboard, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

/**
 * Modal for scanning packages (QR or manual entry)
 * Uses delivery_point_id instead of tracking_code
 * Updates package status to 'with_driver' when scanned by assigned driver
 */
function PackageScannerModal({ isOpen, onClose, driverId, onPackageScanned }) {
  const [mode, setMode] = useState('select'); // 'select' | 'qr' | 'manual'
  const [manualId, setManualId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [backCameraId, setBackCameraId] = useState(null);

  // Detect back camera
  useEffect(() => {
    if (!isOpen || mode !== 'qr') return;
    
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then(devices => {
        const rear = devices.find(d =>
          d.kind === 'videoinput' &&
          /rear|back|environment/i.test(d.label)
        );
        setBackCameraId(rear?.deviceId || null);
      })
      .catch(err => console.error('Camera enumeration failed', err));
  }, [isOpen, mode]);

  // Auto-hide message after 3 seconds
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMode('select');
      setManualId('');
      setMessage(null);
    }
  }, [isOpen]);

  /**
   * Scan package and update status to 'with_driver'
   * @param {string|number} deliveryPointId - Delivery point ID
   */
  const scanPackage = async (deliveryPointId) => {
    if (scanning) return;
    setScanning(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/packages/scan`,
        {
          delivery_point_id: parseInt(deliveryPointId),
          driver_id: driverId,
          new_status: 'with_driver'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { package: pkg, delivery_point } = response.data;

      setMessage({
        type: 'success',
        text: `✓ Paquete recibido - Seq ${delivery_point.sequence}: ${delivery_point.recipient_name}`
      });

      // Notify parent to update UI
      onPackageScanned({
        delivery_point_id: delivery_point.id,
        package_id: pkg.id,
        tracking_code: pkg.tracking_code,
        status: pkg.status,
        sequence: delivery_point.sequence
      });

      // Auto-close after success (with delay for user to see message)
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error scanning package:', error);
      
      const errorMsg = error.response?.data?.error || 'Error al escanear paquete';
      setMessage({
        type: 'error',
        text: errorMsg
      });
    } finally {
      setScanning(false);
    }
  };

  /**
   * Handle QR scan - extract delivery_point_id from QR code
   */
  const handleQrScan = (result) => {
    if (!result || scanning) return;
    const code = typeof result === 'string' ? result : result?.text;
    if (!code) return;
    
    // Extract delivery_point_id from QR code
    // Assuming QR format is just the ID or contains "DP-123" or similar
    const deliveryPointId = code.replace(/\D/g, ''); // Remove non-digits
    
    if (!deliveryPointId) {
      setMessage({ type: 'error', text: 'Código QR inválido' });
      return;
    }
    
    scanPackage(deliveryPointId);
  };

  /**
   * Handle manual entry submission
   */
  const handleManualSubmit = () => {
    if (!manualId.trim()) {
      setMessage({ type: 'error', text: 'Ingrese un ID válido' });
      return;
    }
    scanPackage(manualId.trim());
    setManualId('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-montserrat font-bold text-gray-800">
            Escanear Paquete
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Message Banner */}
        {message && (
          <div className={`m-4 p-4 rounded-lg border-l-4 ${
            message.type === 'success'
              ? 'bg-green-50 border-brand-green text-brand-green'
              : 'bg-red-50 border-red-500 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p className="font-montserrat font-semibold text-sm">
                {message.text}
              </p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Mode Selection */}
          {mode === 'select' && (
            <div className="space-y-4">
              <p className="text-center text-gray-600 font-montserrat mb-6">
                Ingrese el ID del punto de entrega
              </p>

              <button
                onClick={() => setMode('qr')}
                disabled={scanning}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-6 px-6 rounded-xl shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <QrCode size={32} />
                <span className="text-xl">Escanear QR</span>
              </button>

              <button
                onClick={() => setMode('manual')}
                disabled={scanning}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-6 px-6 rounded-xl shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Keyboard size={32} />
                <span className="text-xl">Ingresar ID</span>
              </button>
            </div>
          )}

          {/* QR Scanner Mode */}
          {mode === 'qr' && (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <QrScanner
                  delay={300}
                  onError={e => console.error('QR Scanner error:', e)}
                  onScan={handleQrScan}
                  style={{ width: '100%' }}
                  constraints={{
                    video: backCameraId
                      ? { deviceId: { exact: backCameraId } }
                      : { facingMode: { ideal: 'environment' } }
                  }}
                />
              </div>
              <p className="text-center text-sm text-gray-600">
                Apunte la cámara al código QR del punto de entrega
              </p>
              <button
                onClick={() => setMode('select')}
                disabled={scanning}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-montserrat font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                Cambiar Método
              </button>
            </div>
          )}

          {/* Manual Entry Mode */}
          {mode === 'manual' && (
            <div className="space-y-4">
              {/* Display Area */}
              <div className="bg-gray-100 border-2 border-brand-primary rounded-lg p-6 min-h-[80px] flex items-center justify-center">
                <div className="text-center w-full">
                  {manualId ? (
                    <p className="font-montserrat font-bold text-3xl text-gray-800 tracking-wider">
                      {manualId}
                    </p>
                  ) : (
                    <p className="font-montserrat text-gray-400 text-lg">
                      Ingrese el ID
                    </p>
                  )}
                </div>
              </div>

              {/* Custom Numeric Keypad */}
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                  <button
                    key={num}
                    onClick={() => setManualId(prev => prev + num.toString())}
                    disabled={scanning}
                    className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-2xl py-6 rounded-lg transition-colors border-2 border-gray-200 hover:border-brand-primary disabled:opacity-50"
                  >
                    {num}
                  </button>
                ))}
                
                {/* Clear button */}
                <button
                  onClick={() => setManualId('')}
                  disabled={scanning}
                  className="bg-red-100 hover:bg-red-200 active:bg-red-300 text-red-700 font-montserrat font-semibold text-lg py-6 rounded-lg transition-colors border-2 border-red-200 disabled:opacity-50"
                >
                  Borrar
                </button>
                
                {/* Zero button */}
                <button
                  onClick={() => setManualId(prev => prev + '0')}
                  disabled={scanning}
                  className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-2xl py-6 rounded-lg transition-colors border-2 border-gray-200 hover:border-brand-primary disabled:opacity-50"
                >
                  0
                </button>
                
                {/* Backspace button */}
                <button
                  onClick={() => setManualId(prev => prev.slice(0, -1))}
                  disabled={scanning}
                  className="bg-orange-100 hover:bg-orange-200 active:bg-orange-300 text-orange-700 font-montserrat font-semibold text-lg py-6 rounded-lg transition-colors border-2 border-orange-200 disabled:opacity-50"
                >
                  ←
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleManualSubmit}
                  disabled={!manualId || scanning}
                  className={`flex-1 font-montserrat font-semibold py-4 px-6 rounded-lg transition-all ${
                    manualId && !scanning
                      ? 'bg-brand-primary hover:bg-brand-primary/90 text-white'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {scanning ? 'Procesando...' : 'Confirmar'}
                </button>
                <button
                  onClick={() => {
                    setMode('select');
                    setManualId('');
                  }}
                  disabled={scanning}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-montserrat font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cambiar Método
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PackageScannerModal;